import { MonitorBase } from 'areas/assets/monitors/shared-components/monitorBase';
import { createRoot } from 'react-dom/client';
import {
	CreatedAssetGroupForCachedMonitorKey,
	MonitorForCreatedAssetGroupKey,
} from 'areas/assets/monitors/shared-components/configuration';
import React, { useCallback, useEffect } from 'react';
import { AssetGroupMonitorEditProps, GenericMonitorFactory, i18n, MonitorValue } from './types';
import { observer } from 'mobx-react';
import { ApplicationState } from 'framework/applicationState';
import { Permission } from 'framework/permission';
import { serialize } from 'serializr';
import { NavigationStore } from 'framework/navigationStore';
import { AssetGroupRouter } from 'areas/assetgroups/bundleDescription';
import { AntButton } from 'controls/react/ant/antButton';
import { PlusCircleOutlined } from '@ant-design/icons';

export type AddAssetGroupButtonProps<TMonitor extends MonitorBase<TMonitor>> = AssetGroupMonitorEditProps<TMonitor> & {
	onChange: (targetId: string) => void;
};

export const AddAssetGroupButton = observer(
	<TMonitor extends MonitorBase<TMonitor>>({
		monitor,
		onChange,
		ignoreType,
		onClick,
	}: AddAssetGroupButtonProps<TMonitor>) => {
		const allowCreate = ApplicationState.hasPermissions(Permission.CreateAsset);

		const handleAdd = useCallback(() => {
			if (!allowCreate) return;
			if (typeof monitor === 'function') {
				monitor = monitor();
			}
			const monitorJs = (ignoreType ? monitor : serialize(monitor)) as object;
			localStorage.setItem(MonitorForCreatedAssetGroupKey, JSON.stringify(monitorJs));
			onClick?.();
			NavigationStore.go(AssetGroupRouter.createNew(), {
				extra: {
					mode: 'create',
				},
			});
		}, []);

		useEffect(() => {
			const group = localStorage.getItem(CreatedAssetGroupForCachedMonitorKey);
			if (group) {
				onChange?.(group);
				localStorage.removeItem(CreatedAssetGroupForCachedMonitorKey);
			}
		}, []);

		if (!allowCreate) return null;
		return (
			<AntButton type={'text'} icon={<PlusCircleOutlined />} onClick={handleAdd} title={i18n('Create assetgroup')} />
		);
	},
);

export const appendAddAssetGroupButton = <TMonitor extends MonitorBase<TMonitor>>({
	monitor,
	onChange,
	assetGroupDropDown,
	onClick,
}: {
	monitor: MonitorValue<TMonitor> | GenericMonitorFactory;
	onChange: (targetId: string) => void;
	assetGroupDropDown: kendo.ui.ComboBox;
	onClick?: () => void;
}) => {
	const container = document.createElement('div');
	assetGroupDropDown.element.after(container);
	const root = createRoot(container);
	const handleUpdate = (targetId: string) => {
		assetGroupDropDown.value(targetId);
		onChange(targetId);
	};
	root.render(<AddAssetGroupButton monitor={monitor} onChange={handleUpdate} ignoreType={true} onClick={onClick} />);
	return () => {
		root.unmount();
		container.remove();
	};
};

export * from './types';
